import {
  CubeIcon,
  BucketIcon,
  SourceCodeIcon,
  Database01Icon,
  FileSlidersIcon,
  FileKeyIcon,
  FolderLibraryIcon,
  Globe02Icon,
  ComputerIcon,
  Plug01Icon,
  Radar01Icon,
  Route01Icon,
  ServerStack01Icon,
  UserIcon,
  UserMultipleIcon,
  type TerraIcon,
} from '@terra/ui-icons';
import type { ExternalNetworkEntityKind } from '@terra/shared-contracts-external-network';
import type { InternalNetworkAdEntityKind } from '@terra/ui-graphs/internal-network';

export type ExternalNetworkEntityKindFilter = ExternalNetworkEntityKind | null;
export type InternalNetworkAdEntityKindFilter =
  | InternalNetworkAdEntityKind
  | 'CANONICAL'
  | null;
export type ExternalNetworkFilterItem = {
  key: string;
  type: ExternalNetworkEntityKindFilter;
  labelKey: string;
  icon: TerraIcon;
};
export type InternalNetworkFilterItem = {
  key: string;
  type: InternalNetworkAdEntityKindFilter;
  labelKey: string;
  labelSource?: 'internal' | 'external';
  icon: TerraIcon;
};
export type SurfaceEntityKindTranslator = (key: string) => string;
export const EXTERNAL_NETWORK_ENTITY_FILTER_ITEMS: ExternalNetworkFilterItem[] =
  [
    { key: 'all', type: null, labelKey: 'all', icon: Radar01Icon },
    {
      key: 'netblocks',
      type: 'NETBLOCK',
      labelKey: 'netblock',
      icon: Route01Icon,
    },
    { key: 'domains', type: 'DOMAIN', labelKey: 'domain', icon: Globe02Icon },
    { key: 'hosts', type: 'HOST', labelKey: 'host', icon: ServerStack01Icon },
    {
      key: 'gateways',
      type: 'GATEWAY',
      labelKey: 'gateway',
      icon: ServerStack01Icon,
    },
    {
      key: 'services',
      type: 'NETWORK_SERVICE',
      labelKey: 'service',
      icon: Plug01Icon,
    },
    {
      key: 'software',
      type: 'SOFTWARE',
      labelKey: 'software',
      icon: SourceCodeIcon,
    },
    {
      key: 'storage',
      type: 'STORAGE_SERVICE',
      labelKey: 'storage',
      icon: BucketIcon,
    },
  ];
export const INTERNAL_NETWORK_ENTITY_FILTER_ITEMS: InternalNetworkFilterItem[] =
  [
    { key: 'all', type: null, labelKey: 'all', icon: Radar01Icon },
    {
      key: 'hosts',
      type: 'CANONICAL',
      labelKey: 'host',
      labelSource: 'external',
      icon: Database01Icon,
    },
    { key: 'users', type: 'USER', labelKey: 'user', icon: UserIcon },
    { key: 'groups', type: 'GROUP', labelKey: 'group', icon: UserMultipleIcon },
    {
      key: 'computers',
      type: 'COMPUTER',
      labelKey: 'computer',
      icon: ComputerIcon,
    },
    { key: 'ous', type: 'OU', labelKey: 'ou', icon: FolderLibraryIcon },
    { key: 'domains', type: 'DOMAIN', labelKey: 'domain', icon: Globe02Icon },
    { key: 'gpos', type: 'GPO', labelKey: 'gpo', icon: FileSlidersIcon },
    {
      key: 'domain-controllers',
      type: 'DOMAIN_CONTROLLER',
      labelKey: 'domainController',
      icon: ServerStack01Icon,
    },
    {
      key: 'cert-templates',
      type: 'CERT_TEMPLATE',
      labelKey: 'certTemplate',
      icon: FileKeyIcon,
    },
    {
      key: 'containers',
      type: 'CONTAINER',
      labelKey: 'container',
      icon: CubeIcon,
    },
  ];
export const SURFACE_ASSETS_PAGE_SIZE = 100;
export const EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP: Record<
  ExternalNetworkEntityKind,
  string
> = {
  NETBLOCK: 'purple',
  DOMAIN: 'blue',
  HOST: 'cyan',
  GATEWAY: 'orange',
  NETWORK_SERVICE: 'green',
  SOFTWARE: 'orange',
  STORAGE_SERVICE: 'yellow',
};
export function translateExternalNetworkFilterItems({
  t,
}: {
  t: SurfaceEntityKindTranslator;
}): Array<
  Omit<ExternalNetworkFilterItem, 'labelKey'> & {
    label: string;
  }
> {
  return EXTERNAL_NETWORK_ENTITY_FILTER_ITEMS.map(({ labelKey, ...item }) => ({
    ...item,
    label: t(labelKey),
  }));
}

export function translateInternalNetworkFilterItems({
  t,
  tExternal,
}: {
  t: SurfaceEntityKindTranslator;
  tExternal?: SurfaceEntityKindTranslator;
}): Array<
  Omit<InternalNetworkFilterItem, 'labelKey'> & {
    label: string;
  }
> {
  return INTERNAL_NETWORK_ENTITY_FILTER_ITEMS.map(
    ({ labelKey, labelSource, ...item }) => ({
      ...item,
      label:
        labelSource === 'external' && tExternal
          ? tExternal(labelKey)
          : t(labelKey),
    }),
  );
}

export function getExternalNetworkEntityKindLabel({
  kind,
  t,
}: {
  kind: ExternalNetworkEntityKind;
  t: SurfaceEntityKindTranslator;
}): string {
  const item = EXTERNAL_NETWORK_ENTITY_FILTER_ITEMS.find(
    (filterItem) => filterItem.type === kind,
  );

  return item ? t(item.labelKey) : kind;
}

export function getExternalNetworkEntityKindIcon(
  kind: ExternalNetworkEntityKind,
): TerraIcon {
  return (
    EXTERNAL_NETWORK_ENTITY_FILTER_ITEMS.find((item) => item.type === kind)
      ?.icon ?? Radar01Icon
  );
}

export function getInternalNetworkEntityKindLabel({
  kind,
  t,
}: {
  kind: InternalNetworkAdEntityKind;
  t: SurfaceEntityKindTranslator;
}): string {
  const item = INTERNAL_NETWORK_ENTITY_FILTER_ITEMS.find(
    (filterItem) => filterItem.type === kind,
  );

  return item ? t(item.labelKey) : kind;
}

export function getExternalNetworkEntityCount({
  stats,
  kind,
}: {
  stats:
    | {
        totalDomains?: number;
        totalHosts?: number;
        totalGateways?: number;
        totalServices?: number;
        totalNetblocks?: number;
        totalSoftware?: number;
        totalStorageServices?: number;
      }
    | undefined;
  kind: ExternalNetworkEntityKindFilter;
}): number {
  if (!stats) {
    return 0;
  }

  if (!kind) {
    return (
      (stats.totalDomains ?? 0) +
      (stats.totalHosts ?? 0) +
      (stats.totalGateways ?? 0) +
      (stats.totalServices ?? 0) +
      (stats.totalNetblocks ?? 0) +
      (stats.totalSoftware ?? 0) +
      (stats.totalStorageServices ?? 0)
    );
  }

  const countByKind: Record<ExternalNetworkEntityKind, number> = {
    DOMAIN: stats.totalDomains ?? 0,
    HOST: stats.totalHosts ?? 0,
    GATEWAY: stats.totalGateways ?? 0,
    NETWORK_SERVICE: stats.totalServices ?? 0,
    NETBLOCK: stats.totalNetblocks ?? 0,
    SOFTWARE: stats.totalSoftware ?? 0,
    STORAGE_SERVICE: stats.totalStorageServices ?? 0,
  };

  return countByKind[kind] ?? 0;
}

export type SurfaceAssetScope =
  | {
      targetType: 'EXTERNAL_NETWORK';
      surfaceAssetKind: ExternalNetworkEntityKind;
      entityId: string;
    }
  | {
      targetType: 'INTERNAL_NETWORK';
      surfaceAssetKind: InternalNetworkAdEntityKind | 'ASSET';
      entityId: string;
    };
export function buildSurfaceAssetTabId({
  targetType,
  surfaceAssetKind,
  entityId,
}: SurfaceAssetScope): string {
  return `surface-asset-${targetType}-${surfaceAssetKind}-${entityId}`;
}
